import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FocusService } from 'app/core/services/focus.service';
import { PickerStateService } from 'app/core/services/picker-state.service';
import { PaginatedListState } from 'app/shared/states/paginated-list-state';
import { TableConfig } from './table-config';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-table-wrapper',
  templateUrl: './table-wrapper.component.html',
  styleUrls: ['./table-wrapper.component.scss'],
  imports: [],
})
export class TableWrapperComponent {
  @Output() public keydownAddEvent = new EventEmitter<void>();
  @Output() public keydownArrowLeftEvent = new EventEmitter<void>();
  @Output() public keydownArrowRightEvent = new EventEmitter<void>();
  @Output() public keydownEnterEvent = new EventEmitter<void>();
  @Output() public keydownPageDownEvent = new EventEmitter<void>();
  @Output() public keydownPageUpEvent = new EventEmitter<void>();
  @Input() public paginatedListState: PaginatedListState;
  @Input() public requireFocus = false;
  @Input() public tableConfig: TableConfig;
  @Input() public totalCount: number;
  @ViewChild('_table') private _table: ElementRef;
  public hasFocus = false;
  public indexFocusedRow: number | null;

  private _dialog: MatDialog = inject(MatDialog);
  private _focusService: FocusService = inject(FocusService);
  private _pickerStateService: PickerStateService = inject(PickerStateService);

  public afterRowFocusedHandler(uuid: string): void {
    // Blur other rows and make sure the indexFocusRow is rightely set
    this.tableConfig?.rows.forEach((row, index) => {
      if (row.uuid !== uuid) {
        row.blurRow();
      } else {
        this.indexFocusedRow = index;
      }
    });
  }

  public clearFocus(): void {
    this.hasFocus = false;
  }

  public focusAtIndex(index: number): void {
    // Blur all rows and make sure the indexFocusRow is rightely set
    this.tableConfig?.rows.forEach((row) => {
      row.blurRow();
    });
    this._focusService.setTimeoutOutsideAngular(() => {
      this.indexFocusedRow = index;
      this.tableConfig?.rows[this.indexFocusedRow]?.focusRow();
    });
  }

  public focusOut(): void {
    // Blur all rows and make sure the indexFocusRow is rightely set
    this.tableConfig?.rows.forEach((row) => {
      row.blurRow();
    });
    this.indexFocusedRow = null;
  }

  @HostListener('window:keydown.+', ['$event'])
  @HostListener('window:keydown.shift.+', ['$event'])
  public keydownAdd($event: Event): void {
    const input = $event.target as HTMLInputElement;
    if (input && (input.type === 'text' || (input.type === '' && !input.draggable))) {
      return;
    }
    $event.preventDefault();
    this.keydownAddEvent.emit();
  }

  @HostListener('window:keydown.alt.+', ['$event'])
  @HostListener('window:keydown.alt.shift.+', ['$event'])
  public keydownAltAdd($event: Event): void {
    $event.preventDefault();
    this.keydownAddEvent.emit();
  }

  @HostListener('window:keydown.ArrowDown', ['$event'])
  @HostListener('window:keydown.control.ArrowDown', ['$event'])
  public keydownArrowDown($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'radio' || input.nodeName === 'MAT-SELECT')) {
        return;
      }
      let previousIndex = this.indexFocusedRow;
      if (!previousIndex && !(previousIndex === 0)) {
        previousIndex = 0;
      } else {
        previousIndex = (previousIndex + 1) % this.tableConfig?.rows.length;
      }

      this.focusAtIndex(previousIndex);
    }
  }

  @HostListener('window:keydown.ArrowLeft', ['$event'])
  @HostListener('window:keydown.control.ArrowLeft', ['$event'])
  public keydownArrowLeft($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'text' || (input.type === '' && !input.draggable))) {
        return;
      }

      if (this.paginatedListState) {
        const previousIndex = this.paginatedListState.pageIndex - 1;
        if (previousIndex >= 0) {
          this.paginatedListState.pageIndex = previousIndex;
          this.keydownArrowLeftEvent.emit();
        }
      }
    }
  }

  @HostListener('window:keydown.ArrowRight', ['$event'])
  @HostListener('window:keydown.control.ArrowRight', ['$event'])
  public keydownArrowRight($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'text' || (input.type === '' && !input.draggable))) {
        return;
      }

      if (this.paginatedListState) {
        const nextIndex = this.paginatedListState.pageIndex + 1;
        if (this.paginatedListState.pageSize && this.paginatedListState.pageSize !== 0) {
          const maxIndex = this.totalCount / this.paginatedListState.pageSize;
          if (nextIndex < maxIndex) {
            this.paginatedListState.pageIndex = nextIndex;
            this.keydownArrowRightEvent.emit();
          }
        }
      }
    }
  }

  @HostListener('window:keydown.ArrowUp', ['$event'])
  @HostListener('window:keydown.control.ArrowUp', ['$event'])
  public keydownArrowUp($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'radio' || input.nodeName === 'MAT-SELECT')) {
        return;
      }
      $event.preventDefault();
      let nextIndex = this.indexFocusedRow;
      if (!nextIndex && !(nextIndex === 0)) {
        nextIndex = this.tableConfig?.rows.length - 1;
      } else {
        nextIndex = nextIndex - 1;
        if (nextIndex < 0) {
          nextIndex = this.tableConfig?.rows.length - 1;
        }
      }
      this.focusAtIndex(nextIndex);
    }
  }

  @HostListener('keydown.Enter', ['$event'])
  @HostListener('keydown.control.Enter', ['$event'])
  public keydownEnter($event: Event): void {
    if (
      (this.indexFocusedRow || this.indexFocusedRow === 0) &&
      this._table.nativeElement.contains(document.activeElement) &&
      (!this.requireFocus || this.hasFocus)
    ) {
      const input = $event.target as HTMLButtonElement;
      if (input && (input.type === 'button' || input.type === 'submit')) {
        return;
      }

      this.tableConfig?.rows[this.indexFocusedRow].keydownEnterEvent.emit();
      this.keydownEnterEvent.emit();
    }
  }

  @HostListener('window:keydown.PageDown', ['$event'])
  public keydownPageDown($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'text' || (input.type === '' && !input.draggable))) {
        return;
      }
      this.keydownPageDownEvent.emit();
    }
  }

  @HostListener('window:keydown.PageUp', ['$event'])
  public keydownPageUp($event: Event): void {
    if (this.proceedEvent($event) && (!this.requireFocus || this.hasFocus)) {
      const input = $event.target as HTMLInputElement;
      if (input && (input.type === 'text' || (input.type === '' && !input.draggable))) {
        return;
      }
      this.keydownPageUpEvent.emit();
    }
  }

  @HostListener('window:keydown.tab', ['$event'])
  public keydownTab(event: Event): void {
    const targetElement = event.target as HTMLElement;

    // Check if the click was outside the element
    if (targetElement && !this._table.nativeElement.contains(targetElement)) {
      this.focusOut();
    }
  }

  @HostListener('window:click', ['$event'])
  public onClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    // Check if the click was outside the element
    if (targetElement && !this._table.nativeElement.contains(targetElement)) {
      this.focusOut();
    }
  }

  public proceedEvent($event: Event): boolean {
    // If a mat-autocomplete of a picker is open somewhere, return false to ignore the keyboard event.
    if (this._pickerStateService.autocompleteOpenedFlag) {
      return false;
    }

    // If an app-picker-dialog is open
    if (this._dialog.openDialogs.length > 0) {
      // Search for an optional closest picker dialog in the parents of the current element.
      const closestPicker = this._table.nativeElement.closest('app-picker-dialog');

      // If this instance of the table-wrapper is not child of the picker-dialog,
      // return false to ignore the keyboard event.
      if (!closestPicker) {
        return false;
      }
      // else (this instance of the table-wrapper is a child of the app-picker-dialog)
      // preventDefault and return true to handle the keyboard event.
      else {
        $event.preventDefault();
      }
    }
    return true;
  }

  public setFocus(): void {
    this.hasFocus = true;
  }
}
